/**
 * ColorSwatchPicker: the eight curated swatches (src/lib/mapColors.ts
 * UNIT_COLOR_SWATCHES) plus a free hex field, shared by every row of
 * MapColorsTab (Admin > Units > Map colours: one unit's color, and each
 * of the tenant's map colors) and SpiceKindColorsTab (Admin > Units >
 * SPICE colours: the two SPICE session kinds).
 *
 * Pure display + onChange. Validation (is this hex well-formed? contrast
 * against the paper base? too close to another chosen color? readable
 * text on it?) is the caller's job, since only the caller knows the FULL
 * set of colors in play and what each one is for.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { UNIT_COLOR_SWATCHES, isValidHexColor } from '@/lib/mapColors';

export interface ColorSwatchPickerProps {
  label: string;
  value: string;
  onChange: (hex: string) => void;
  /** Shown under the field when set (e.g. a contrast or ΔE warning). The
   *  picker itself never blocks the change; the caller decides whether
   *  anything holds its Save back. */
  warning?: string | null;
  /** Shown under the field as an alert when set: a choice the caller
   *  refuses to save (the caller holds its Save back while it shows). */
  error?: string | null;
  disabled?: boolean;
}

export function ColorSwatchPicker({
  label,
  value,
  onChange,
  warning,
  error,
  disabled = false,
}: ColorSwatchPickerProps): ReactNode {
  const [hexDraft, setHexDraft] = useState(value);
  const hexInvalid = hexDraft !== '' && !isValidHexColor(hexDraft);

  // Re-sync the draft field whenever the caller's value moves (a swatch
  // click, or a Reset) — this component only calls onChange with a
  // VALID hex, so a half-typed invalid draft never gets clobbered:
  // `value` doesn't change until the draft itself becomes valid.
  useEffect(() => {
    setHexDraft(value);
  }, [value]);

  return (
    <div className="flex flex-col gap-1.5" data-testid={`color-picker-${label}`}>
      <div className="flex items-center gap-2">
        <span
          aria-hidden="true"
          className="inline-block h-5 w-5 shrink-0 rounded-full border border-sage-800/[15%]"
          style={{ backgroundColor: isValidHexColor(value) ? value : 'transparent' }}
          data-testid={`color-picker-${label}-swatch`}
        />
        <span className="text-sm font-medium text-ink">{label}</span>
      </div>
      <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label={`${label} swatch choices`}>
        {UNIT_COLOR_SWATCHES.map((s) => (
          <button
            key={s.hex}
            type="button"
            title={s.label}
            aria-label={s.label}
            aria-pressed={value.toUpperCase() === s.hex.toUpperCase()}
            disabled={disabled}
            data-testid={`color-picker-${label}-swatch-${s.label.toLowerCase().replace(/\s+/g, '-')}`}
            onClick={() => {
              setHexDraft(s.hex);
              onChange(s.hex);
            }}
            className={
              'h-7 w-7 rounded-full border-2 transition-colors ' +
              (value.toUpperCase() === s.hex.toUpperCase() ? 'border-sage-800' : 'border-transparent hover:border-sage-400/50')
            }
            style={{ backgroundColor: s.hex }}
          />
        ))}
        <input
          type="text"
          value={hexDraft}
          disabled={disabled}
          placeholder="#1B4332"
          aria-label={`${label} hex value`}
          data-testid={`color-picker-${label}-hex`}
          onChange={(e) => {
            const next = e.target.value;
            setHexDraft(next);
            if (isValidHexColor(next)) onChange(next);
          }}
          className="w-24 min-h-8 rounded-base border border-sage-300 bg-paper px-2 text-sm text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
        />
      </div>
      {hexInvalid && (
        <p className="text-xs text-danger" data-testid={`color-picker-${label}-hex-error`}>
          Not a hex color. Try something like #1B4332.
        </p>
      )}
      {error && (
        <p role="alert" className="text-xs text-danger" data-testid={`color-picker-${label}-error`}>
          {error}
        </p>
      )}
      {warning && (
        <p className="text-xs text-warm-700" data-testid={`color-picker-${label}-warning`}>
          {warning}
        </p>
      )}
    </div>
  );
}
